/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { FixedSizeList as List } from 'react-window';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import ExerciseDataBaseButton from './ExerciseDataBaseButton';
import TBTooltip from '../../../../shared/components/TBTooltip/TBTooltip';
import Text from '../../../../shared/components/Text/Text';
import {
  setIsCloneDragging,
  setActiveModal,
  addPreSavedItemToCalendarItemsArray,
  addPreSavedItemToProgramItemsArray,
  setIsPrimaryAnimatedSideBarOpen,
  setPrimarySideBarDataTag,
  setSecondarySideBarDataTag,
  setSideBarExerciseSearchTerm,
} from '../../ducks/calendarActions';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  height: 100%;
  width: 100%;
`;

const SearchAndIcon = styled('div')`
  display: flex;
  align-items: center;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  background: white;
  overflow: hidden;
  width: 100%;
  min-height: 32px;
`;

const SearchIconContainer = styled('div')`
  padding-left: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 5px;
  height: 40px;
`;

const SearchContainer = styled('input')`
  display: flex;
  border: none;
  background: none;
  padding-left: 5px;
  width: 100%;
  height: 40x;

  ::placeholder {
  font-size: 16px;
  font-family: 'Nunito Sans';
  color: #9E9E9E;
}
`;

const ExerciseWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 8px;
  overflow: auto;
  max-height: ${(props) => props.sideBarContainerHeight - 160 - props.expandedTitlesContainerHeight}px;
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 0.5);
    border-radius: 10px;
    transition: background-color 0.3s ease; 
  }
  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0); 
  }
  :hover::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 1); 
  }
  scrollbar-width: thin; 
  scrollbar-color: rgba(136, 136, 136, 0.5) transparent;
`;

const ItemContainer = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 4px;
  transition: all 250ms ease-in-out;
  }
`;

const ExerciseNameAndIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  background-color: ${(props) => props.backgroundColor};
  border-radius: 4px;
  :hover {
    opacity: 0.7;
  }
`;

const DroppableDiv = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
  min-height: 0; /* Prevents extra height during dragging */
`;

const TextOverflowEllipse = styled('h3')`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  font-family: 'Nunito Sans';
  font-size:  ${(props) => (props.fontSize ? props.fontSize : '16px')};
  color: ${(props) => (props.fontColor ? props.fontColor : '#444444')};
  margin-bottom: 0px;
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : '700')};
  cursor: inherit;
  transition: all 250ms ease-in-out;
  &:hover {
    opacity: '0.7';
  }
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height:  ${(props) => (props.textContainerHeight ? props.textContainerHeight : 'none')};
  max-width: ${(props) => (props.textContainerMaxWidth ? props.textContainerMaxWidth : '200px')};
  cursor: pointer;
`;

const IconHoverWrapper = styled('div')`
  transition: all 250ms ease-in-out;
    &:hover {
      opacity: 0.7;
    }
`;

const QuestionnairesContainer = ({
  sideBarContainerHeight,
  expandedTitlesContainerHeight,
  sideBarContainerWidth,
  windowWidth,
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchedQuestionnaires, setSearchedQuestionnaires] = useState([]);
  const exerciseWrapperRef = useRef(null);
  const [draggingCloneIndex, setDraggingCloneIndex] = useState(null);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const isCloneDragging = useSelector((state) => state.calendar.data.isCloneDragging);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const date = new Date(selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');

  const questionnaires = useSelector(
    (state) => state.calendar.data.calendarItemContainers.questionnaireCalendarItems,
    shallowEqual,
  );

  useEffect(() => {
    /** Normalize strings by converting them to lowercase and removing extra spaces */
    const normalize = (str) => str.toLowerCase().trim();

    /** Split the search term into individual words for partial matching */
    const searchWords = normalize(searchTerm).split(/\s+/);

    const filteredExercises = questionnaires?.filter((obj) => {
      const exerciseName = normalize(obj.name);
      /** Check if every word in the search matches some part of the exercise name */
      return searchWords.every((word) => exerciseName.includes(word));
    }) || [];

    /** Add "Create New Exercise" option if necessary */
    if (
      searchTerm
      && questionnaires?.length
      && !filteredExercises.some((obj) => normalize(obj.name) === normalize(searchTerm))
    ) {
      filteredExercises.push({ id: 'create-new-exercise', name: 'Create New Questionnaire' });
    }
    if (!questionnaires?.length) {
      filteredExercises.push({ id: 'create-new-exercise', name: 'Create New Questionnaire' });
    }

    /** Update the state with the new array */
    setSearchedQuestionnaires(filteredExercises);
  }, [searchTerm, questionnaires]);

  useEffect(() => {
    dispatch(setSideBarExerciseSearchTerm(searchTerm));
  }, [searchTerm]);

  const Item = ({ index, style }) => {
    const item = searchedQuestionnaires[index];
    return (
      <>
        {item.id === 'create-new-exercise' ? (
          <ItemContainer style={{ ...style, borderTop: '1px solid #EEEEEE', cursor: 'pointer' }} onClick={() => dispatch(setActiveModal('createNewComponentModal', { type: 'S' }))}>
            <ExerciseNameAndIconWrapper>
              <IconBox backgroundColor='rgba(155, 89, 182, 0.2)'>
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='science'
                  color='#9B59B6'
                />
              </IconBox>
              <Text fontSize='16px' style={{ whiteSpace: 'nowrap' }} fontWeight={700}>Create New Questionnaire</Text>
            </ExerciseNameAndIconWrapper>
            {sideBarContainerWidth > 383 && (
            <Text color='#9E9E9E' fontSize='16px'>Shift+Enter</Text>
            )}
          </ItemContainer>
        ) : (
          <Draggable key={item.id} draggableId={`${item.id}-sideBar`} index={index}>
            {(draggableProvided) => (
              <div
                ref={draggableProvided.innerRef}
                {...draggableProvided.draggableProps}
                {...draggableProvided.dragHandleProps}
                style={{
                  ...style,
                }}
              >
                <ItemContainer
                  style={{ cursor: windowWidth < 768 && 'pointer' }}
                  onClick={windowWidth < 768 ? () => {
                    const newItem = {
                      ...item,
                      icon: 'science',
                      title: item.name,
                      exerciseId: parseInt(item.id, 10),
                      id: `${item.id}-${generateUniqueId()}`,
                      assignedId: `${item.id}-${generateUniqueId()}`,
                      preSave: true,
                    };
                    if (programType === 'free-form-program') {
                      dispatch(addPreSavedItemToProgramItemsArray(newItem, selectedProgramDayNumber));
                    } else {
                      dispatch(addPreSavedItemToCalendarItemsArray(newItem, endpointFormattedDate));
                    }
                    if (windowWidth < 768) {
                    /** close the side bar because it is full width at that containerWidth */
                      dispatch(setIsPrimaryAnimatedSideBarOpen(false));
                      dispatch(setPrimarySideBarDataTag(null));
                      dispatch(setSecondarySideBarDataTag(null));
                    }
                  } : null}
                >
                  <ExerciseNameAndIconWrapper>
                    <IconBox backgroundColor='rgba(155, 89, 182, 0.2)'>
                      <IcomoonReact
                        iconSet={iconSet}
                        size={15}
                        icon='science'
                        color='#9B59B6'
                      />
                    </IconBox>
                    <TBTooltip
                      tooltipContainerRef={exerciseWrapperRef}
                      textToDisplay={item.name}
                      textToHover={item.name}
                      addHoverOpacity
                      fontSize='16px'
                      fontWeight={400}
                      toolTipFontSize='13px'
                      toolTipFontWeight={400}
                      textContainerPadding='0px'
                      textContainerMaxWidth={`${sideBarContainerWidth - 100}px`}
                    />
                  </ExerciseNameAndIconWrapper>
                  {item.media ? (
                    <IconHoverWrapper>
                      <IcomoonReact
                        iconSet={iconSet}
                        size={16}
                        icon='video-camera'
                        color='#9E9E9E'
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation(); /** Prevents the container's onClick from firing */
                          dispatch(setActiveModal('createNewComponentModal', {
                            type: 'S', editModal: true, disabledEditModal: true, itemInfoForEditModal: item,
                          }));
                        }}
                      />
                    </IconHoverWrapper>
                  ) : (
                    <IconHoverWrapper>
                      <IcomoonReact
                        iconSet={iconSet}
                        size={16}
                        icon='info2'
                        color='#9E9E9E'
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation(); /** Prevents the container's onClick from firing */
                          dispatch(setActiveModal('createNewComponentModal', {
                            type: 'S', editModal: true, disabledEditModal: true, itemInfoForEditModal: item,
                          }));
                        }}
                      />
                    </IconHoverWrapper>
                  )}
                </ItemContainer>
              </div>
            )}
          </Draggable>
        )}
        {isCloneDragging && draggingCloneIndex === index && (
        <div
          style={{
            ...style,
          }}
        >
          <ItemContainer>
            <ExerciseNameAndIconWrapper>
              <IconBox backgroundColor='rgba(155, 89, 182, 0.2)'>
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='science'
                  color='#9B59B6'
                />
              </IconBox>
              <TBTooltip
                tooltipContainerRef={exerciseWrapperRef}
                textToDisplay={item.name}
                textToHover={item.name}
                addHoverOpacity
                fontSize='16px'
                fontWeight={400}
                toolTipFontSize='13px'
                toolTipFontWeight={400}
                textContainerPadding='0px'
                textContainerMaxWidth={`${sideBarContainerWidth - 100}px`}
              />
            </ExerciseNameAndIconWrapper>
            {item.media ? (
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='video-camera'
                color='#9E9E9E'
              />
            ) : (
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='info2'
                color='#9E9E9E'
              />
            )}
          </ItemContainer>
        </div>
        )}
      </>
    );
  };

  return (
    <Wrapper sideBarContainerWidth={sideBarContainerWidth}>
      <SearchAndIcon id='search-warm-up-container' onClick={() => setIsSearchFocused(true)} isFocused={isSearchFocused}>
        <SearchIconContainer id='search-warm-up-icon-container'>
          <IcomoonReact
            iconSet={iconSet}
            size={18}
            icon='magnifying-glass-alt'
            color='#9E9E9E'
          />
        </SearchIconContainer>
        <SearchContainer
          placeholder='Search for an exercise'
          id='search-container-input'
          value={searchTerm}
          onFocus={() => setIsSearchFocused(true)}
          onChange={(inputValue) => {
            setSearchTerm(inputValue.target.value);
          }}
        />
        {searchTerm && (
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color='#9E9E9E'
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => setSearchTerm('')}
        />
        )}
      </SearchAndIcon>
      <ExerciseWrapper ref={exerciseWrapperRef} sideBarContainerHeight={sideBarContainerHeight} expandedTitlesContainerHeight={expandedTitlesContainerHeight}>
        <Droppable
          droppableId='questionnaireCalendarItems'
          isDropDisabled
          mode='virtual'
          renderClone={(
            provided,
            snapshot,
            rubric,
          ) => {
            /** NOTE: We intentionally use useEffect in renderClone here despite it violating React rules.
            This provides smoother drag animations without visual flashing.
            The alternative setTimeout approach causes a noticeable white flash.
            This only executes during active drags, so the performance impact is negligible. */
            useEffect(() => {
              if (snapshot.isDragging) {
                dispatch(setIsCloneDragging(snapshot.isDragging));
                setDraggingCloneIndex(rubric.source.index);
              }
            }, [snapshot.isDragging, rubric.source.index, snapshot.isDropAnimating]);

            return (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...provided.draggableProps.style,
                  border: '1px dashed #77A7FF',
                  borderRadius: '4px',
                  background: 'white',
                  opacity: 1,
                }}
              >
                <ItemContainer>
                  <ExerciseNameAndIconWrapper>
                    <IconBox backgroundColor='rgba(155, 89, 182, 0.2)'>
                      <IcomoonReact
                        iconSet={iconSet}
                        size={15}
                        icon='science'
                        color='#9B59B6'
                      />
                    </IconBox>
                    <TextContainer textContainerMaxWidth={`${sideBarContainerWidth - 100}px`}>
                      <TextOverflowEllipse fontSize='16px' fontWeight={400}>
                        {searchedQuestionnaires[rubric.source.index].name}
                      </TextOverflowEllipse>
                    </TextContainer>
                  </ExerciseNameAndIconWrapper>
                </ItemContainer>
              </div>
            );
          }}
        >
          {(droppableProvided) => (
            <DroppableDiv
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              <List
                height={sideBarContainerHeight - 160 - expandedTitlesContainerHeight}
                itemCount={searchedQuestionnaires?.length}
                itemSize={40}
                width='100%'
              >
                {Item}
              </List>
            </DroppableDiv>
          )}
        </Droppable>
      </ExerciseWrapper>
      <ExerciseDataBaseButton onClick={() => dispatch(setActiveModal('createNewComponentModal', { type: 'S' }))} type='Questionnaire' />
    </Wrapper>
  );
};

export default React.memo(QuestionnairesContainer);
